import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UploadDocumentModalComponent } from '../components/modals/upload-document-modal/upload-document-modal.component';
import { UploadChunkModalComponent } from '../components/modals/upload-chunk-modal/upload-chunk-modal.component';
import { ActivateDocumentModalComponent } from '../components/modals/activate-document-modal/activate-document-modal.component';
import { DeleteTextModalComponent } from '../components/modals/delete-text-modal/delete-text-modal.component';
import { DeleteDocumentModalComponent } from '../components/modals/delete-document-modal/delete-document-modal.component';
import { DeleteChatModalComponent } from '../components/modals/delete-chat-modal/delete-chat-modal.component';
import { EditTextModalComponent } from '../components/modals/edit-text-modal/edit-text-modal.component';
import { EditConversationNameModalComponent } from '../components/modals/edit-conversation-name-modal/edit-conversation-name-modal.component';
import { ForgotPasswordModalComponent } from '../../login/components/modals/forgot-password-modal/forgot-password-modal.component';
import { ManualDocumentsModalComponent } from '../components/modals/manual-documents-modal/manual-documents-modal.component';
import { PromptsGalleryModalComponent } from '../components/modals/prompts-gallery-modal/prompts-gallery-modal.component';
import { ViewDocumentModalComponent } from '../components/modals/view-document-modal/view-document-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private modalService: NgbModal) { }

  openUploadDocumentModal(
    collectionName: string,
    isReadOnly: boolean = false,
    documentName?: string,
    categories: string[] = []
  ): Promise<{ uploads: Array<{ documentName: string; file: File; category: string }> }> {
    const modalRef = this.modalService.open(UploadDocumentModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.isReadOnly = isReadOnly;
    if (documentName && isReadOnly) {
      modalRef.componentInstance.documentName = documentName;
    }
    modalRef.componentInstance.collectionName = collectionName;
    modalRef.componentInstance.categories = categories;

    return modalRef.result;
  }

  openUploadChunkModal(collectionName: string): Promise<any> {
    const modalRef = this.modalService.open(UploadChunkModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'custom-large-modal'
    });

    modalRef.componentInstance.collectionName = collectionName;

    modalRef.componentInstance.chunkUploadConfirmed.subscribe((result: { chunkName: string, chunkContent: string }) => {
      modalRef.close(result);
    });

    return modalRef.result;
  }

  openActivateDocumentModal(collectionName: string, documentName: string): Promise<boolean> {
    const modalRef = this.modalService.open(ActivateDocumentModalComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.collectionName = collectionName;
    modalRef.componentInstance.documentName = documentName;

    modalRef.componentInstance.activateConfirmed.subscribe(() => {
      modalRef.close(true);
    });

    return modalRef.result;
  }
  openDeleteConfirmModal(
    itemName: string, 
    title: string = 'Delete Document',
    message: string = 'Are you sure you want to delete this document? This action cannot be undone.'
  ): Promise<boolean> {
    const modalRef = this.modalService.open(DeleteTextModalComponent, {
      centered: true,
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'plain-modal'
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.itemName = itemName;

    modalRef.componentInstance.deleteConfirmed.subscribe(() => {
      modalRef.close(true);
    });

    return modalRef.result;
  }

  openDeleteTextModal(heading: string): Promise<boolean> {
    const modalRef = this.modalService.open(DeleteTextModalComponent, {
      centered: true,
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'plain-modal'
    });

    // Set the title and message for text deletion
    modalRef.componentInstance.title = 'Delete Text';
    modalRef.componentInstance.message = 'Are you sure you want to delete this text entry from the knowledge base? The assistant will no longer use this data for answering queries.';
    modalRef.componentInstance.itemName = heading;

    modalRef.componentInstance.deleteConfirmed.subscribe(() => {
      modalRef.close(true);
    });

    return modalRef.result;
  }

  openDeleteDocumentModal(documentName: string): Promise<boolean> {
    const modalRef = this.modalService.open(DeleteDocumentModalComponent, {
      centered: true,
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'plain-modal'
    });

    modalRef.componentInstance.documentName = documentName;

    modalRef.componentInstance.deleteConfirmed.subscribe(() => {
      modalRef.close(true);
    });

    return modalRef.result;
  }

  openEditTextModal(initial: { heading: string; text: string }): Promise<{ heading: string; text: string }> {
    const modalRef = this.modalService.open(EditTextModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'custom-large-modal'
    });

    modalRef.componentInstance.heading = initial.heading;
    modalRef.componentInstance.text = initial.text;

    modalRef.componentInstance.saveConfirmed.subscribe((payload: { heading: string; text: string }) => {
      modalRef.close(payload);
    });

    return modalRef.result;
  }

  openDeleteChatModal(chatTitle?: string): Promise<boolean> {
    const modalRef = this.modalService.open(DeleteChatModalComponent, {
      centered: true,
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'plain-modal'
    });

    modalRef.componentInstance.chatTitle = chatTitle;

    modalRef.componentInstance.deleteConfirmed.subscribe(() => {
      modalRef.close(true);
    });

    return modalRef.result;
  }

  openEditConversationNameModal(currentName: string): Promise<string> {
    const modalRef = this.modalService.open(EditConversationNameModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'custom-large-modal'
    });

    modalRef.componentInstance.currentName = currentName;

    modalRef.componentInstance.saveConfirmed.subscribe((name: string) => {
      modalRef.close(name);
    });

    return modalRef.result;
  }

  openChangePasswordModal(): Promise<any> {
    const modalRef = this.modalService.open(ForgotPasswordModalComponent, {
      centered: true,
      size: 'sm',
      backdrop: true,
      keyboard: true
    });

    return modalRef.result;
  }

  openManualDocumentsModal(): Promise<any> {
    const modalRef = this.modalService.open(ManualDocumentsModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: true
    });

    return modalRef.result;
  }

  openPromptsGalleryModal(): Promise<any> {
    const modalRef = this.modalService.open(PromptsGalleryModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: true,
      windowClass: 'prompts-gallery-modal'
    });

    return modalRef.result;
  }

  openViewDocumentModal(collectionName: string, document: any): Promise<void> {
    const modalRef = this.modalService.open(ViewDocumentModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.collectionName = collectionName;
    modalRef.componentInstance.document = document;

    return modalRef.result;
  }

  dismissAllModals(): void {
    this.modalService.dismissAll();
  }

}